import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AccountListCacheService } from '../../../@theme/services/account-list-cache.service';
import { TransactionStateService } from '../../../pages/transaction-list/transaction-state.service';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';


@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private unsubscribe: Subscription[] = [];
  public authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  private resetPasswordAttempts = 0;
  private readonly MAX_RESET_ATTEMPTS = 3;

  currentUser$: Observable<AuthModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<AuthModel>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): AuthModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: AuthModel) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private http: HttpClient,
    private accountListCache: AccountListCacheService,
    private transactionState: TransactionStateService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<AuthModel>({} as AuthModel);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }
  login(credentials: string): Observable<any> {
    this.isLoadingSubject.next(true);
    // Reset password attempts on successful login flow start
    this.resetPasswordAttempts = 0;
    
    return this.authHttpService.login(credentials).pipe(
      map((auth: AuthModel) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    // Check if user is on password reset page - defer logout if under attempt limit
    if (this.isOnPasswordResetFlow() && this.resetPasswordAttempts < this.MAX_RESET_ATTEMPTS) {
    //  console.log(`[AUTH SERVICE] On reset page with ${this.resetPasswordAttempts} attempts - deferring logout`);
      return; // Don't logout yet
    }
    
    // Normal logout flow or max attempts reached
  //  console.log('[AUTH SERVICE] Performing logout');
    this.resetPasswordAttempts = 0; // Reset counter
    localStorage.removeItem(this.authLocalStorageToken);
    this.clearCachedData();
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  /**
   * Clear cached view data (e.g. the account list) that may contain PII, so a
   * different user signing in on the same tab never sees the prior user's data.
   * The list lives in Cache Storage (survives reload), so it must be wiped
   * explicitly here.
   */
  private clearCachedData(): void {
    void this.accountListCache.clear();
    // All Transactions Status / Transaction ID reset to their defaults.
    this.transactionState.clearSessionFilters();
  }

  /**
   * Check if user is currently on password reset/change flow
   * Covers two scenarios:
   * 1. Email-based reset (has userId/email + code/token params)
   * 2. Logged-in user password change (on reset page without params)
   */
  private isOnPasswordResetFlow(): boolean {
    if (typeof window === 'undefined') {
      return false; // SSR safety
    }

    const currentUrl = window.location.pathname;
    const queryParams = new URLSearchParams(window.location.search);
    
    const isResetPage = currentUrl.includes('/reset-password') || 
                        currentUrl.includes('/reset-pass') ||
                        currentUrl.includes('/change-password');
    
    // Return true if on any password reset/change page, regardless of query params
    // This covers both email link reset AND logged-in user password change
    return isResetPage;
  }

  /**
   * Increment password attempt counter (call this on failed password reset/change)
   * Allows 3 attempts with error messages, then forces logout on attempt >= 3
   */
  public incrementPasswordAttempt(): void {
    if (!this.isOnPasswordResetFlow()) {
      return; // Only track attempts on password reset pages
    }

    this.resetPasswordAttempts++;
    console.log(`[AUTH SERVICE] Password attempt ${this.resetPasswordAttempts}/${this.MAX_RESET_ATTEMPTS}`);

    if (this.resetPasswordAttempts >= this.MAX_RESET_ATTEMPTS) {
      console.log('[AUTH SERVICE] Max password attempts reached (${this.resetPasswordAttempts}) - forcing logout');
      this.forceLogout();
    }
  }

  /**
   * Reset the password attempt counter (call this on successful password reset)
   */
  public resetPasswordAttemptCounter(): void {
    this.resetPasswordAttempts = 0;
    console.log('[AUTH SERVICE] Password reset attempts counter reset');
  }

  /**
   * Force logout without attempt checking (used when max attempts reached)
   */
  private forceLogout(): void {
    localStorage.removeItem(this.authLocalStorageToken);
    this.clearCachedData();
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<AuthModel | undefined> {
    const auth = this.getAuthFromLocalStorage();
  
    if (!auth || !auth.token) {
      this.isLoadingSubject.next(false); // Properly update loading state
     this.logout();
      return of(undefined);
    }
  
    this.isLoadingSubject.next(true);
    this.currentUserSubject.next(auth);
    return of(auth); // Always returns an Observable synchronously
  }
  
 /*  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  } */

    forgotPassword(email: string): Observable<boolean> {
      this.isLoadingSubject.next(true);
      return this.authHttpService
        .forgotPassword(email)
        .pipe(finalize(() => this.isLoadingSubject.next(false)));
    } 
  private setAuthFromLocalStorage(auth: AuthModel): boolean {

       if (auth && auth.token) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }
  
  refreshUserToken(): Observable<AuthModel> {
    // Get current user from local storage
    const authData = this.getAuthFromLocalStorage();
    if (!authData || !authData.token || !authData.refreshtoken) {
      return throwError(() => new Error('No valid user session or refresh token found.'));
    }
    
    return this.authHttpService.refreshToken(authData.refreshtoken, authData.token).pipe(
      map((tokens: any) => {
        if (!tokens || !tokens.token || !tokens.refreshtoken) {
          throw new Error('Invalid token response received from server.');
        }
        
        // Preserve the existing user fields (email, role, etc.) and only swap
        // in the fresh tokens. Building a bare AuthModel here previously dropped
        // the email, which blanked it in the header on any screen that
        // triggered a token refresh.
        const updatedUser = Object.assign(new AuthModel(), authData);
        updatedUser.token = tokens.token;
        updatedUser.refreshtoken = tokens.refreshtoken; // Note: lowercase 't' in refreshtoken from API
        updatedUser.expiresIn = new Date(new Date().getTime() + (tokens.Expires_in || 3600) * 1000);
        
        localStorage.setItem(this.authLocalStorageToken, JSON.stringify(updatedUser));
        this.currentUserSubject.next(updatedUser);
  
        return updatedUser;
      }),
      catchError((error) => {
        console.error('Error refreshing token:', error.message || error);
        return throwError(() => new Error('Failed to refresh token. Please log in again.'));
      })
    );
  }
  
  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
 
      if (!lsValue) {
        return undefined;
      }
  
      const authData = JSON.parse(lsValue);
      return authData; // Ensure valid object return
    } catch (error) {
      console.error('Error parsing token from localStorage:', error);
      return undefined;
    }
  }
  

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
